import { useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import { toast } from "sonner";
import { Play, Loader2, Sparkles, RotateCcw, Terminal as TermIcon, Save, ClipboardCheck, CheckCircle2, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { bitEvent, openBit } from "@/lib/bit";

const MONACO_LANG = {
  python: "python",
  javascript: "javascript",
  html: "html",
  cpp: "cpp",
  java: "java",
  php: "php",
  bash: "shell",
  powershell: "powershell",
  xml: "xml",
  pearl: "plaintext",
  ki: "plaintext",
  text: "plaintext",
};

const LABELS = {
  python: "Python",
  javascript: "JavaScript",
  html: "HTML / CSS",
  cpp: "C++",
  java: "Java",
  php: "PHP",
  bash: "Linux / Bash",
  powershell: "Windows / PowerShell",
  xml: "XML",
  pearl: "PEARL",
  ki: "KI-Prompting",
  text: "Text",
};

export default function CodeRunner({ language, initialCode = "", task = null, checkUrl = null, taskPassed = false, onTaskPassed, lessonTitle = "" }) {
  const [code, setCode] = useState(initialCode);
  const [output, setOutput] = useState("");
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isError, setIsError] = useState(false);
  const [checking, setChecking] = useState(false);
  const [taskResult, setTaskResult] = useState(null);
  const iframeRef = useRef(null);
  const isHtml = language === "html";
  const isServerLang = ["cpp", "java", "python", "php", "bash", "powershell", "xml", "ki"].includes(language);
  const isBrowserRunnable = isHtml || language === "javascript" || isServerLang;
  const isPassed = taskPassed || taskResult?.passed;

  const reset = () => {
    setCode(initialCode);
    setOutput("");
    setIsError(false);
  };

  const runJs = () => {
    const logs = [];
    const customConsole = {
      log: (...a) => logs.push(a.map(fmt).join(" ")),
      error: (...a) => logs.push(a.map(fmt).join(" ")),
      warn: (...a) => logs.push(a.map(fmt).join(" ")),
      info: (...a) => logs.push(a.map(fmt).join(" ")),
    };
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function("console", code);
      fn(customConsole);
      setIsError(false);
      setOutput(logs.join("\n") || "(keine Ausgabe – nutze console.log)");
    } catch (e) {
      setIsError(true);
      setOutput(`${logs.join("\n")}${logs.length ? "\n" : ""}Fehler: ${e.message}`);
    }
  };

  const fmt = (v) => {
    if (typeof v === "object") {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }
    return String(v);
  };

  const runHtml = () => {
    setOutput("");
    setIsError(false);
    if (iframeRef.current) {
      iframeRef.current.srcdoc = code;
    }
  };

  const runServer = async () => {
    setOutput("Wird kompiliert & ausgeführt...");
    try {
      const { data } = await api.post("/run", { language, code });
      if (data.compile_error) {
        setIsError(true);
        setOutput("Kompilierfehler:\n" + (data.stderr || ""));
        return;
      }
      let out = data.stdout || "";
      if (data.timed_out) {
        setIsError(true);
        setOutput((out ? out + "\n" : "") + "Zeitüberschreitung – das Programm lief zu lange.");
        return;
      }
      if (data.stderr) out += (out ? "\n" : "") + data.stderr;
      setIsError(data.exit_code !== 0);
      setOutput(out.trim() || "(keine Ausgabe)");
    } catch (e) {
      setIsError(true);
      setOutput(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const saveSnippet = async () => {
    const title = window.prompt("Titel für dein Code-Snippet:");
    if (!title) return;
    setSaving(true);
    try {
      await api.post("/snippets", { title, language, code });
      toast.success("Snippet gespeichert ✓");
      window.dispatchEvent(new CustomEvent("snippet-saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const checkTaskFn = async () => {
    if (!checkUrl) return;
    try {
      if (typeof window !== "undefined" && window.speechSynthesis?.paused) {
        window.speechSynthesis.resume();
      }
    } catch {
      // ignore
    }
    setChecking(true);
    bitEvent("task-checking", { task, code, language, lessonTitle });
    try {
      const { data } = await api.post(checkUrl, { code });
      setTaskResult(data);
      const bitContext = `Sprache: ${LABELS[language]}\n\nCode:\n${code}${data.stderr ? `\n\nFehlermeldung:\n${data.stderr}` : ""}`;
      if (data.passed) {
        toast.success("Aufgabe gelöst!");
        bitEvent("task-solved", {
          task,
          code,
          language,
          lessonTitle,
          message: data.message,
          xp: data.xp_awarded || 0,
        });
        onTaskPassed?.();
      } else {
        toast.warning("Noch nicht ganz richtig – schau dir das Feedback unten an.");
        bitEvent(/Kompilierfehler/.test(data.message || "") ? "compile-error" : "fail", { context: bitContext });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setChecking(false);
    }
  };

  const run = async () => {
    setRunning(true);
    try {
      if (language === "javascript") runJs();
      else if (language === "html") runHtml();
      else if (isServerLang) await runServer();
    } finally {
      setRunning(false);
    }
  };

  const explainWithAi = () => {
    openBit({
      context: `Sprache: ${LABELS[language]}\n\nCode:\n${code}`,
      message: "Kannst du mir diesen Code Zeile für Zeile erklären?",
    });
  };

  return (
    <div id="code-runner-container" className="rounded-xl border-2 border-black nb-shadow overflow-hidden bg-[#0D0D12]">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-1 px-2 sm:px-3 py-2 bg-[#16161d] border-b-2 border-black">
        <div className="flex items-center gap-1.5 text-[#F8F8F2] shrink-0">
          <TermIcon className="h-4 w-4 text-primary" strokeWidth={2.5} />
          <span className="font-mono text-xs font-bold">{LABELS[language] || language}</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={reset}
            data-testid="code-reset"
            className="flex items-center gap-1 text-[#aaa] hover:text-white text-xs font-bold p-1.5 rounded transition-colors"
            title="Zurücksetzen"
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
          <button
            onClick={saveSnippet}
            disabled={saving}
            data-testid="code-save"
            className="flex items-center gap-1 text-[#aaa] hover:text-white text-xs font-bold p-1.5 rounded transition-colors disabled:opacity-50"
            title="Snippet speichern"
          >
            <Save className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
          <button
            onClick={explainWithAi}
            data-testid="code-explain-ai"
            title="KI erklärt"
            className="flex items-center gap-1 bg-secondary text-white text-xs font-bold px-2 sm:px-3 py-1.5 rounded-lg border-2 border-black nb-shadow-sm nb-press"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span className="hidden sm:inline">KI erklärt</span>
          </button>
          {isBrowserRunnable && (
            <button
              onClick={run}
              disabled={running}
              data-testid="code-editor-run"
              title="Ausführen"
              className="flex items-center gap-1 bg-success text-black text-xs font-bold px-2 sm:px-3 py-1.5 rounded-lg border-2 border-black nb-shadow-sm nb-press disabled:opacity-60"
            >
              {running ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
              ) : (
                <Play className="h-3.5 w-3.5" strokeWidth={2.5} />
              )}
              <span className="hidden sm:inline">Ausführen</span>
            </button>
          )}
          {task && checkUrl && (
            <button
              onClick={checkTaskFn}
              disabled={checking}
              data-testid="code-check-task"
              title="Aufgabe prüfen"
              className={`flex items-center gap-1 text-xs font-bold px-2 sm:px-3 py-1.5 rounded-lg border-2 border-black nb-shadow-sm nb-press disabled:opacity-60 ${
                isPassed ? "bg-success text-black" : "bg-primary text-white"
              }`}
            >
              {checking ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
              ) : isPassed ? (
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
              ) : (
                <ClipboardCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
              )}
              <span className="hidden sm:inline">{isPassed ? "Gelöst" : "Aufgabe prüfen"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Task instructions */}
      {task && (
        <div className="px-4 py-3 bg-warning border-b-2 border-black" data-testid="task-instructions">
          <p className="text-[10px] font-mono font-bold text-black/60 mb-1">AUFGABE</p>
          <p className="text-sm font-medium text-black">{task.instructions}</p>
        </div>
      )}

      {/* Editor */}
      <Editor
        height="280px"
        language={MONACO_LANG[language] || "plaintext"}
        theme="vs-dark"
        value={code}
        onChange={(v) => setCode(v ?? "")}
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          fontFamily: "'JetBrains Mono', monospace",
          scrollBeyondLastLine: false,
          padding: { top: 12 },
          lineNumbers: "on",
          automaticLayout: true,
          wordWrap: "on",
          wrappingIndent: "same",
        }}
      />

      {/* Output */}
      {isHtml ? (
        <div className="bg-white border-t-2 border-black">
          <div className="px-3 py-1 text-[10px] font-mono font-bold bg-[#16161d] text-[#aaa]">VORSCHAU</div>
          <iframe
            ref={iframeRef}
            title="html-preview"
            data-testid="html-preview"
            className="w-full h-48 bg-white"
            sandbox="allow-scripts"
          />
        </div>
      ) : isBrowserRunnable ? (
        <div className="bg-[#0D0D12] border-t-2 border-black px-4 py-3 min-h-[80px]">
          <div className="text-[10px] font-mono font-bold text-[#666] mb-1">{language === "ki" ? "ANTWORT" : "KONSOLE"}</div>
          <pre
            data-testid="code-output"
            className={`font-mono text-sm leading-relaxed whitespace-pre-wrap break-words ${isError ? "text-error" : "text-[#F8F8F2]"}`}
          >
            {output || (language === "ki" ? "Klicke auf 'Ausführen', um deinen Prompt an die KI zu senden." : "Klicke auf 'Ausführen', um deinen Code zu starten.")}
          </pre>
        </div>
      ) : (
        <div className="bg-[#0D0D12] border-t-2 border-black px-4 py-3 min-h-[80px]">
          <div className="text-[10px] font-mono font-bold text-[#666] mb-1">HINWEIS</div>
          <p className="font-mono text-sm leading-relaxed text-[#aaa]">
            Übe hier frei. Eine direkte Ausführung ist für diese Sprache nicht verfügbar – probiere es anschließend in deinem eigenen Terminal/Tool aus.
          </p>
        </div>
      )}

      {/* Task feedback */}
      {task && taskResult && (
        <div
          className={`flex items-start gap-2 px-4 py-3 border-t-2 border-black text-sm font-bold ${
            taskResult.passed ? "bg-success text-black" : "bg-error text-white"
          }`}
          data-testid="task-feedback"
        >
          {taskResult.passed ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={2.5} />
          ) : (
            <XCircle className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={2.5} />
          )}
          <span>{taskResult.message}</span>
        </div>
      )}
    </div>
  );
}
